import { ReactNode, useMemo, useState } from "react"

import gsap from "gsap"
import styled, { CSSProp } from "styled-components"

import Waves from "components/Banners/Wave"
import InnerWrapper from "components/InnerWrapper"
import ToolTip from "components/ToolTip"
import AnimatedPath from "components/UI-Related/AnimatedPaths"
import { ReactComponent as StatPathTurq } from "images/neobanking/StatPath.svg"
import { ReactComponent as StatPathMag } from "images/neobanking/StatPathMag.svg"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import { ColorType } from "types"
import useAnimation from "utils/useAnimation"
import useMedia from "utils/useMedia"

interface Wave {
  bottomStopColors: ColorType[]
  topStopColors: ColorType[]
}

interface KickerProps {
  text: string
  width?: string
  size?: (typeof text)[keyof typeof text]
}

interface TitleProps {
  text: string
  size?: (typeof text)[keyof typeof text]
}

interface Tooltip {
  text: string | ReactNode
  position?: string
  overrideMargin?: string
}

interface Props {
  linesTurq?: boolean
  stopColors: ColorType[]
  wave: Wave
  title: TitleProps
  kicker: KickerProps
  tooltip?: Tooltip
  contentPosition?: string
}

export default function Large({
  linesTurq = false,
  wave,
  kicker,
  title,
  stopColors,
  tooltip = undefined,
  contentPosition = undefined,
}: Props) {
  const [wrapperEl, setWrapperEl] = useState<HTMLElement | null>(null)
  const [titleRef, setTitleRef] = useState<HTMLHeadingElement | null>(null)
  const [textRef, setTextRef] = useState<SVGTextElement | null>(null)
  const titleHeight = useMedia("192", "192", "93", "58")
  const [leftPercent, setLeftPercent] = useState(0)

  const textEls = useMemo(
    () =>
      title.text.split("").map((item, i) => {
        const num = i
        return (
          <Character key={`${item}-${num}`} className="char">
            {item}
          </Character>
        )
      }),
    [title]
  )

  useAnimation(() => {
    if (wrapperEl && titleRef) {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapperEl,
          start: "top 85%",
        },
        onComplete: () => {
          titleRef.style.overflow = "visible"
        },
      })

      tl.fromTo(
        ".char",
        {
          attr: {
            y: "2.6em",
          },
        },
        {
          duration: 0.5,
          stagger: 0.1,
          attr: {
            y: "0.9em",
          },
          ease: "sine.out",
        }
      )

      return () => {
        tl.revert()
      }
    }
  }, [wrapperEl, titleRef])

  const colorEls = useMemo(
    () =>
      stopColors.map((item, index, arr) => {
        const offset = (100 / arr.length) * (index + 1)

        return (
          <stop
            // eslint-disable-next-line react/no-array-index-key
            key={`${item}-${index}`}
            stopColor={item}
            offset={`${offset}%`}
          />
        )
      }),
    [stopColors]
  )

  return (
    <Wrapper ref={ref => setWrapperEl(ref)}>
      <Waves
        top
        bottomStopColors={wave.bottomStopColors}
        topStopColors={wave.topStopColors}
      />
      <InnerWrapper>
        <Content>
          <PathWrapper>
            <LeftGradient />
            <AnimatedPath>
              {linesTurq ? <StatPathTurq /> : <StatPathMag />}
            </AnimatedPath>
            <RightGradient />
          </PathWrapper>
          <InnerContent
            contentPosition={contentPosition}
            width={kicker.width ?? "100%"}
          >
            <Kicker>
              <KickerText size={kicker.size}>{kicker.text}</KickerText>
            </Kicker>
            {tooltip && (
              <TooltipWrapper overrideMargin={tooltip.overrideMargin}>
                <ToolTip
                  bannerText={tooltip.text}
                  position={tooltip.position}
                  overrideMargin={tooltip.overrideMargin}
                />
              </TooltipWrapper>
            )}
          </InnerContent>
          <Title ref={ref => setTitleRef(ref)} size={title.size}>
              <SVG
                viewBox={`0 0 1200 ${titleHeight}`}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient
                    id="large-stat-gradient"
                    x1="0"
                    x2="100%"
                    y1="0"
                    y2="0"
                  >
                    {colorEls}
                  </linearGradient>
                </defs>
                <Text
                  ref={ref => setTextRef(ref)}
                  fill="url(#large-stat-gradient)"
                >
                  {textEls}
                </Text>
              </SVG>
          </Title>
        </Content>
      </InnerWrapper>
    </Wrapper>
  )
}

const Character = styled.tspan`
  position: relative;
`

const Wrapper = styled.section`
  position: relative;
  z-index: 4;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  ${media.desktop} {
    padding-bottom: 9.04vw;
  }

  ${media.fullWidth} {
    padding-bottom: 150px;
  }

  ${media.tablet} {
    padding-bottom: 15vw;
  }

  ${media.mobile} {
    padding-bottom: 15.67vw;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  position: relative;

  ${media.fullWidth} {
    gap: 76px;
  }

  ${media.desktop} {
    gap: 4.58vw;
  }

  ${media.tablet} {
    gap: 4.492vw;
  }

  ${media.mobile} {
    gap: 9.267vw;
  }
`

const PathWrapper = styled.div`
  position: absolute;
  z-index: -1;
  height: auto;

  ${media.fullWidth} {
    top: -410px;
    width: 1500px;
  }

  ${media.desktop} {
    top: -17.7vw;
    width: 90.36vw;
  }

  ${media.tablet} {
    top: -10vw;
    width: 93.85vw;
  }

  ${media.mobile} {
    display: none;
  }
`

const LeftGradient = styled.div`
  position: absolute;
  width: 6.024vw;
  height: 5vw;
  top: 7vw;
  left: 0vw;
  z-index: 1;
  background: linear-gradient(90deg, #f5f6ff 0%, rgba(245, 246, 255, 0) 100%);

  ${media.fullWidth} {
    width: 100px;
    height: 83px;
    top: 116px;
    left: 0px;
  }

  ${media.tablet} {
    top: 7vw;
    left: 0vw;
  }

  ${media.mobile} {
    display: none;
  }
`

const RightGradient = styled.div`
  position: absolute;
  width: 6.024vw;
  height: 7vw;
  left: 85vw;
  top: -2vw;
  z-index: 1;
  background: linear-gradient(90deg, #f5f6ff 0%, rgba(245, 246, 255, 0) 100%);
  transform: rotate(-180deg);

  ${media.fullWidth} {
    width: 100px;
    height: 116px;
    left: 1411px;
    top: -33px;
  }

  ${media.tablet} {
    left: 89vw;
    top: -2vw;
  }

  ${media.mobile} {
    display: none;
  }
`

const InnerContent = styled.div<{ width?: string; contentPosition?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.lightMode};
  width: ${({ width }) => width || "50vw"};
  left: ${({ contentPosition }) => contentPosition || "unset"};

  ${media.fullWidth} {
    margin-top: -386.97px;
    min-height: 138.02px;
  }

  ${media.desktop} {
    margin-top: -16.31vw;
    min-height: 8.31vw;
  }

  ${media.tablet} {
    min-height: 8vw;
    margin-top: -8vw;
  }

  ${media.mobile} {
    flex-direction: column;
    margin-top: -30vw;
    min-height: 22.933vw;
  }
`

const Kicker = styled.div`
  display: flex;
  position: relative;

  ${media.fullWidth} {
    gap: 25px;
  }

  ${media.desktop} {
    gap: 1.51vw;
  }

  ${media.tablet} {
    gap: 4.49vw;
  }

  ${media.mobile} {
    gap: 6.667vw;
  }
`

const KickerText = styled.p<{ size?: CSSProp }>`
  ${props => (props.size ? props.size : text.h5)}
  color: ${colors.blue800};
  text-align: center;

  ${media.tablet} {
    ${props => (props.size ? props.size : text.h6)};
    text-align: center;
  }

  ${media.mobile} {
    ${props => (props.size ? props.size : text.bodyR)};
    text-align: center;
  }
`

const TooltipWrapper = styled.div<{ overrideMargin?: string }>`
  align-self: center;

  ${media.fullWidth} {
    margin: ${({ overrideMargin }) => overrideMargin || "0 20px"};
  }

  ${media.desktop} {
    margin: ${({ overrideMargin }) => overrideMargin || "0 1.2vw"};
  }

  ${media.tablet} {
    margin: ${({ overrideMargin }) => overrideMargin || "0 1.95vw"};
  }

  ${media.mobile} {
    margin: ${({ overrideMargin }) => overrideMargin || "2.67vw 0"};
  }
`

const Title = styled.h2<{ size?: CSSProp }>`
  ${props => (props.size ? props.size : text.h1)};
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  height: auto;
  overflow: hidden;
  position: relative;

  ${media.fullWidth} {
    height: 192px;
  }

  ${media.desktop} {
    height: 11.57vw;
  }

  ${media.tablet} {
    ${props => (props.size ? props.size : text.h2)};
    height: 9.05vw;
  }

  ${media.mobile} {
    ${props => (props.size ? props.size : text.h4)};
    height: 15.47vw;
  }
`
const SVGContainer = styled.div`
  display: flex;
  justify-content: center;
`

const SVG = styled.svg`
  width: 100%;
  height: 100%;
  position: block;
  display: flex;
  justify-content: center;
  text-align: center;
  // margin-left: calc(50% - 800px / 2);
`

const Text = styled.text`
  position: relative;
  display: inline-block;
  width: fit-content;
`
